import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';

import AgGrid from '@components/AgGrid/AgGrid';

import { convertObjectToString } from '@utils';

const RevisionHistoryLog = ({ revisionHistoryList, selectedId, onClose }) => {
  const [showChanged, setShowChanged] = useState(false);

  const revisionRecord = revisionHistoryList.find(
    i => i && i.revisionHistoryID === selectedId,
  );

  const {
    tableName,
    userAction,
    beforeValue,
    afterValue,
    metaData,
    changeBy,
    changeDt,
  } = revisionRecord;

  // eslint-disable-next-line no-nested-ternary
  const rowData = beforeValue && Object.keys(beforeValue).length
    ? Object.keys(beforeValue)
        .filter(i => i !== 'eventData')
        .map(i => ({
          dataColumn: i,
          beforeData: beforeValue && convertObjectToString(beforeValue[i]),
          afterData: afterValue && convertObjectToString(afterValue[i]),
        }))
        .concat(
          beforeValue.eventData
            ? Object.keys(beforeValue.eventData).map(i => ({
                dataColumn: i,
                beforeData:
                  beforeValue.eventData &&
                  convertObjectToString(beforeValue.eventData[i]),
                afterData:
                  afterValue.eventData &&
                  convertObjectToString(afterValue.eventData[i]),
              }))
            : [],
        )
    : afterValue && Object.keys(afterValue).length
    ? Object.keys(afterValue)
        .filter(i => i !== 'eventData')
        .map(i => ({
          dataColumn: i,
          beforeData: beforeValue && convertObjectToString(beforeValue[i]),
          afterData: afterValue && convertObjectToString(afterValue[i]),
        }))
        .concat(
          afterValue.eventData
            ? Object.keys(afterValue.eventData).map(i => ({
                dataColumn: i,
                beforeData:
                  beforeValue.eventData &&
                  convertObjectToString(beforeValue.eventData[i]),
                afterData:
                  afterValue.eventData &&
                  convertObjectToString(afterValue.eventData[i]),
              }))
            : [],
        )
    : [];

  const sortedRowData = rowData.sort((a, b) =>
    a.dataColumn.toLowerCase() > b.dataColumn.toLowerCase() ? 1 : -1,
  );

  const getColumnDefs = () => [
    {
      headerName: 'Data Columns',
      field: 'dataColumn',
    },
    {
      headerName: 'Before Data',
      field: 'beforeData',
      valueFormatter: ({ value }) => JSON.stringify(value)
    },
    {
      headerName: 'After Data',
      field: 'afterData',
      valueFormatter: ({ value }) => JSON.stringify(value)
    },
  ];

  return (
    <Container>
      <HeaderContainer>
        <Title>
          {selectedId} | {tableName} {userAction}
        </Title>
      </HeaderContainer>
      <MetadataContainer>
        <MetadataColumn>
          <ValueRow>
            <Label>Date</Label>
            <span>{changeDt}</span>
          </ValueRow>
          <ValueRow>
            <Label>Username</Label>
            <span>{changeBy}</span>
          </ValueRow>
        </MetadataColumn>
        <MetadataColumn>
          <ValueRow>
            <Label>Table</Label>
            <span>{tableName}</span>
          </ValueRow>
          <ValueRow>
            <Label>Action</Label>
            <span>{userAction}</span>
          </ValueRow>
        </MetadataColumn>
        <MetadataColumn>
          MetaData:
          <br />
          {convertObjectToString(metaData, ', ')}
        </MetadataColumn>
      </MetadataContainer>
      <TableContainer className="ag-theme-material">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showChanged}
                color="primary"
                value="showChanged"
                onChange={() => setShowChanged(!showChanged)}
              />
            }
            label="Show only columns that changed"
          />
        </div>
        <AgGrid
          autoResizeToFit
          defaultColDef={{ resizable: true, sortable: true }}
          name="eventRevisionHistoryLog"
          disablePreferenceSave
          columnDefs={getColumnDefs()}
          rowData={
            showChanged
              ? sortedRowData.filter(i => i.beforeData !== i.afterData)
              : sortedRowData
          }
          rowHeight={35}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '25px',
          }}
        >
          <Button color="primary" variant="contained" onClick={onClose}>
            Close
          </Button>
        </div>
      </TableContainer>
    </Container>
  );
};

RevisionHistoryLog.propTypes = {
  revisionHistoryList: PropTypes.arrayOf(PropTypes.object),
  selectedId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

const Container = styled.div`
  height: 100%;
  padding-bottom: 100px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 56px;
  font-family: Roboto, sans-serif;
  visibility: ${({ preview }) => (preview ? 'hidden' : 'visible')};
`;

const Title = styled.h4`
  flex: ${({ flex }) => flex || 1};
  font-family: Roboto, sans-serif;
  visibility: visible;
  font-size: 24px;
  font-weight: 500;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.87);
`;

const MetadataContainer = styled.div`
  display: flex;
  margin: 20px;
`;

const MetadataColumn = styled.div`
  flex: 1;
  flex-direction: column;
  margin-right: 20px;
`;

const ValueRow = styled.div`
  display: flex;
  flex-direction: row;

  > span {
    color: blue;
  }
`;

const Label = styled.div`
  width: 100px;
`;

const TableContainer = styled.div`
  height: calc(100vh - 400px);
  margin: 20px;
`;

export default RevisionHistoryLog;
